import React, { useState, useRef, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { Badge, Tooltip } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import PropTypes from 'prop-types';
import ExamService from '../../../services/exam/exam.service';
import './styles.css';

const actionBtnStyle = (bg) => ({ backgroundColor: bg });

const MENU_WIDTH = 160;
const MENU_ITEM_HEIGHT = 28; // slim button height
const MENU_PADDING = 16; // top + bottom padding

const HoverableBadge = ({ row, handle_OnShowReport, handle_OnDownloadReport, handle_OnShowPoints, handle_AddContact, handle_EditExam, isKiosk, hasGuruAttribute }) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const [tooltipContent, setTooltipContent] = useState('No data...');
  const [menuStyle, setMenuStyle] = useState({});

  const timerRef = useRef(null);
  const isHovering = useRef(false);
  const isMounted = useRef(true);
  const toggleRef = useRef(null);
  const menuRef = useRef(null);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, []);

  const tooltipId = `hoverable-badge-tooltip-${row.clinicianAssessmentId}`;

  const isLocked = row.locked === 1;
  const isGuruType = row.examType.toLowerCase() === 'guided' || row.examType.toLowerCase() === 'guided_selling';
  // ES-169: kiosk users get the same option under the "Search" name; they are not
  // provisioned with the GURU data attributes, so the attribute check doesn't apply to them.
  const showPoints = isGuruType && row.status.toLowerCase() === 'complete' && (hasGuruAttribute || isKiosk);

  // Count visible menu items to estimate height
  const itemCount =
    (row.status.toLowerCase() === 'complete' ? 1 : 0) +        // View
    (showPoints ? 1 : 0) +                                      // Points / Search
    (row.status.toLowerCase() === 'complete' ? 1 : 0) +        // Download
    (!isKiosk ? 1 : 0) +                                        // Contact
    (!isLocked && row.status.toLowerCase() === 'complete' ? 1 : 0) + // Edit
    (!isKiosk && !isLocked ? 1 : 0);                            // Delete

  const estimatedMenuHeight = itemCount * MENU_ITEM_HEIGHT + MENU_PADDING;
  // ES-169: a kiosk row that is not complete has no applicable action (View/Search/Download/Edit
  // need "complete", Contact/Delete are hidden for kiosk), which used to open an empty menu box.
  const hasMenuItems = itemCount > 0;

  const calculateMenuStyle = useCallback(() => {
    if (!toggleRef.current) return {};
    const rect = toggleRef.current.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;
    const gap = 2;

    // Horizontal: align right edge of menu with right edge of toggle;
    // if that pushes it off-screen left, align left edges instead.
    let left = rect.right - MENU_WIDTH;
    if (left < 8) left = rect.left;
    if (left + MENU_WIDTH > viewportWidth - 8) left = viewportWidth - MENU_WIDTH - 8;

    // Vertical: open downward if there's room, otherwise open upward.
    const spaceBelow = viewportHeight - rect.bottom - gap;
    const spaceAbove = rect.top - gap;
    let top;
    if (spaceBelow >= estimatedMenuHeight || spaceBelow >= spaceAbove) {
      top = rect.bottom + gap;
    } else {
      top = rect.top - estimatedMenuHeight - gap;
    }

    return {
      position: 'fixed',
      top: Math.max(8, top),
      left,
      zIndex: 9999,
      minWidth: `${MENU_WIDTH}px`,
      borderRadius: '12px',
    };
  }, [estimatedMenuHeight]);

  const openDropdown = () => {
    setMenuStyle(calculateMenuStyle());
    setDropdownOpen(true);
    setTooltipOpen(false);
    isHovering.current = false;
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  const closeDropdown = () => setDropdownOpen(false);

  const handleToggleDropdown = () => {
    dropdownOpen ? closeDropdown() : openDropdown();
  };

  // Close on scroll or resize
  useEffect(() => {
    if (!dropdownOpen) return;
    window.addEventListener('scroll', closeDropdown, true);
    window.addEventListener('resize', closeDropdown);
    return () => {
      window.removeEventListener('scroll', closeDropdown, true);
      window.removeEventListener('resize', closeDropdown);
    };
  }, [dropdownOpen]);

  // Close on outside click
  useEffect(() => {
    if (!dropdownOpen) return;
    const handleClickOutside = (e) => {
      const insideToggle = toggleRef.current && toggleRef.current.contains(e.target);
      const insideMenu = menuRef.current && menuRef.current.contains(e.target);
      if (!insideToggle && !insideMenu) {
        closeDropdown();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [dropdownOpen]);

  const handleMouseEnter = () => {
    if (dropdownOpen) return;
    isHovering.current = true;
    timerRef.current = setTimeout(async () => {
      if (isHovering.current) {
        try {
          const response = await ExamService.getPreview(row.clinicianAssessmentId);
          if (!isMounted.current) return;
          if (!response.data || response.data.length === 0) {
            setTooltipContent('No data...');
          } else {
            const formattedContent = response.data
              .map(item => `<div><strong>${item.questionText}:</strong> <span>${item.selection}</span></div>`)
              .join('');
            setTooltipContent(formattedContent);
          }
        } catch (error) {
          if (!isMounted.current) return;
          console.error('Error fetching preview:', error);
          setTooltipContent('Error al cargar los datos');
        }
        if (isMounted.current) setTooltipOpen(true);
      }
    }, 1500);
  };

  const handleMouseLeave = () => {
    isHovering.current = false;
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    setTooltipOpen(false);
  };

  const withClose = (fn) => (e) => {
    fn(e, row);
    closeDropdown();
  };

  const examTypeLower = row.examType.toLowerCase();

  // Portal menu — rendered at document.body to escape any overflow/z-index constraints
  const portalMenu = dropdownOpen
    ? ReactDOM.createPortal(
        <div ref={menuRef} className="dropdown-menu show border-0 p-2 shadow" style={menuStyle}>
          {row.status.toLowerCase() === 'complete' && (
            <div className="action-menu-item p-0 mb-1" onClick={withClose(handle_OnShowReport)}>
              <button style={actionBtnStyle('#27bcfd')}>
                <FontAwesomeIcon icon="eye" className="mr-2" /> View
              </button>
            </div>
          )}
          {showPoints && (
            <div className="action-menu-item p-0 mb-1" onClick={withClose(handle_OnShowPoints)}>
              <button style={actionBtnStyle('#6f42c1')}>
                <FontAwesomeIcon icon={isKiosk ? 'search' : 'star'} className="mr-2" /> {isKiosk ? 'Search' : 'Points'}
              </button>
            </div>
          )}
          {row.status.toLowerCase() === 'complete' && (
            <div className="action-menu-item p-0 mb-1" onClick={withClose(handle_OnDownloadReport)}>
              <button style={actionBtnStyle('#2c7be5')}>
                <FontAwesomeIcon icon="arrow-down" className="mr-2" /> Download
              </button>
            </div>
          )}
          {(
            <div className="action-menu-item p-0 mb-1" onClick={withClose(handle_AddContact)}>
              <button style={actionBtnStyle('#00d27a')}>
                <FontAwesomeIcon icon="sticky-note" className="mr-2" /> Contact
              </button>
            </div>
          )}
          {!isLocked && row.status.toLowerCase() === 'complete' && (
            <div className="action-menu-item p-0 mb-1" onClick={(e) => { handle_EditExam(e, row, 'edit'); closeDropdown(); }}>
              <button style={actionBtnStyle('#ff9800')}>
                <FontAwesomeIcon icon="edit" className="mr-2" /> Edit
              </button>
            </div>
          )}
          {!isKiosk && !isLocked && (
            <hr className="my-1 mx-2" style={{ borderColor: '#e0e0e0' }} />
          )}
          {!isKiosk && !isLocked && (
            <div className="action-menu-item p-0" onClick={(e) => { handle_EditExam(e, row, 'delete'); closeDropdown(); }}>
              <button style={actionBtnStyle('#f44336')}>
                <FontAwesomeIcon icon="trash-alt" className="mr-2" /> Delete
              </button>
            </div>
          )}
        </div>,
        document.body
      )
    : null;

  if (examTypeLower === 'form' || examTypeLower === 'guided') {
    if (!hasMenuItems) return null;
    return (
      <>
        <button
          ref={toggleRef}
          className="btn btn-link text-600 btn-reveal mr-3"
          aria-label="More options"
          aria-expanded={dropdownOpen}
          id={tooltipId}
          onClick={handleToggleDropdown}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          style={{ background: 'none', border: 'none', cursor: 'pointer', lineHeight: 1, padding: '6px 10px' }}
        >
          <FontAwesomeIcon icon="ellipsis-v" className="fs--1" />
        </button>
        {portalMenu}
        <Tooltip placement="top" isOpen={tooltipOpen} target={tooltipId} innerClassName="custom-tooltip">
          <div dangerouslySetInnerHTML={{ __html: tooltipContent }} />
        </Tooltip>
      </>
    );
  }

  // Non-form/guided: only show View badge when complete
  if (row.status.toLowerCase() !== 'complete') return null;
  return (
    <>
      <Badge
        color="info"
        className="ml-2 fs--1"
        pill
        style={{ cursor: 'pointer' }}
        onClick={e => handle_OnShowReport(e, row)}
        id={tooltipId}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        View
      </Badge>
      <Tooltip placement="top" isOpen={tooltipOpen} target={tooltipId} innerClassName="custom-tooltip">
        <div dangerouslySetInnerHTML={{ __html: tooltipContent }} />
      </Tooltip>
    </>
  );
};

HoverableBadge.propTypes = {
  row: PropTypes.object.isRequired,
  handle_OnShowReport: PropTypes.func.isRequired,
  handle_OnDownloadReport: PropTypes.func.isRequired,
  handle_OnShowPoints: PropTypes.func,
  handle_AddContact: PropTypes.func.isRequired,
  handle_EditExam: PropTypes.func.isRequired,
  isKiosk: PropTypes.bool,
  hasGuruAttribute: PropTypes.bool,
};

export default HoverableBadge;
